/**
 * Fait : la partie « La famille » de l'écran d'administration — qui a un
 *   compte, avec quel rôle, accès ouvert ou fermé, et le bouton qui ferme ou
 *   rouvre.
 *
 *   FERMER DEMANDE DEUX CLICS sur le même bouton, le second nommant la
 *   personne. Le produit n'a ni fenêtre de confirmation ni motif à saisir, et
 *   ce n'est pas ici qu'on en invente : deux clics suffisent à empêcher le
 *   geste réflexe, et le libellé qui change dit exactement ce qui va arriver.
 *   Rouvrir, lui, se fait d'un seul clic — c'est le geste dangereux qui se
 *   confirme, pas l'autre.
 *
 * Ne fait pas : elle n'appelle pas l'API — le parent le fait et recharge — et
 *   n'efface aucun compte : aucune route ne le permet pour un tiers.
 */

import type { RoleDeCompte } from '@aymoai/shared';

import { Partie } from '../gabarits/Partie.tsx';
import type { CompteDeLaFamille } from './api.ts';

/** Le rôle, dit en français plutôt qu'en nom de code. */
export const NOM_DU_ROLE: Readonly<Record<RoleDeCompte, string>> = {
  family_admin: 'Parent administrateur',
  member: 'Membre',
};

const AIDE =
  'Fermer un accès coupe tout immédiatement : la personne ne peut plus ' +
  'entrer, et ses conversations en cours se ferment. Rien n’est effacé, et ' +
  'vous pouvez rouvrir.';

export function SectionDeLaFamille({
  comptes,
  aConfirmer,
  enCours,
  refDuTitre,
  surGeste,
}: {
  readonly comptes: readonly CompteDeLaFamille[];

  /** L'identifiant du compte dont le bouton attend son second clic. */
  readonly aConfirmer: string | null;

  readonly enCours: boolean;
  readonly refDuTitre: React.Ref<HTMLHeadingElement>;
  surGeste: (compte: CompteDeLaFamille) => void;
}) {
  return (
    <Partie
      etiquette="Qui est dans l’atelier"
      nature="gens"
      titre="La famille"
      niveauDeTitre={2}
      refDuTitre={refDuTitre}
      aide={AIDE}
      data-role="partie-famille"
    >
      <ul className="rangees" data-liste="famille">
        {comptes.map((compte) => (
          <li
            key={compte.id}
            data-compte={compte.id}
            data-actif={String(compte.actif)}
            data-fondateur={String(compte.fondateur)}
          >
            <strong>{compte.nomAffiche}</strong>
            <span className="texte-attenue">{compte.adresseElectronique}</span>
            <span className="texte-attenue">— {NOM_DU_ROLE[compte.role]}</span>
            <span data-role="etat-du-compte">{compte.actif ? 'accès ouvert' : 'ACCÈS FERMÉ'}</span>

            {/*
              LE BOUTON SE DÉCIDE SUR L'ÉTAT, PAS SUR LA MARQUE.
              La version d'avant n'affichait aucun bouton pour un fondateur,
              fermé comme ouvert : un compte fondateur fermé par une
              intervention sur le serveur ne pouvait alors PLUS être rouvert
              depuis l'atelier, alors que le serveur l'accepte — et deux
              documents affirmaient le contraire.
            */}
            {compte.fondateur && compte.actif ? (
              <span className="texte-attenue" data-role="compte-fondateur">
                Ce compte a fondé la famille : son accès ne se ferme pas depuis l’écran.
              </span>
            ) : (
              <button
                type="button"
                data-role={aConfirmer === compte.id ? 'confirmation' : 'geste-d-acces'}
                disabled={enCours}
                onClick={() => surGeste(compte)}
              >
                {compte.actif
                  ? aConfirmer === compte.id
                    ? `Confirmer : fermer l’accès de ${compte.nomAffiche}`
                    : 'Fermer l’accès'
                  : 'Rouvrir l’accès'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </Partie>
  );
}
